import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PromptBody } from "api/schema/insights/prompts";
import { ThreadMessages } from "api/schema/insights/threads";
import { AxiosError } from "axios";
import { usePublicApiService } from "contexts/PublicApiServiceProvider";
import { useState } from "react";
import { generateTemporaryMessageId } from "utils/messageUtils";

import { threadMessagesKeys } from "./useGetThreadMessages";

export const useCreateThreadMessage = ({
  companyId,
  threadId,
}: {
  companyId: string;
  threadId: string;
}) => {
  const [displayedText, setDisplayedText] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [hasStreamed, setHasStreamed] = useState(false);
  const publicApi = usePublicApiService();
  const queryClient = useQueryClient();
  const { mutate: startStreaming, reset } = useMutation<
    void,
    AxiosError,
    Pick<PromptBody, "prompt">
  >({
    mutationFn: ({ prompt }) =>
      publicApi.createMessageForThread(
        {
          companyId,
          threadId,
        },
        {
          prompt,
          streamOutput: true,
        },
        (chunk) => setDisplayedText((prev) => prev + chunk)
      ),
    onMutate: ({ prompt }) => {
      setIsStreaming(true);
      setDisplayedText("");
      queryClient.setQueryData<ThreadMessages>(
        threadMessagesKeys.one({ companyId, threadId }),
        (oldData) =>
          oldData
            ? [
                ...oldData,
                {
                  id: generateTemporaryMessageId(),
                  role: "user",
                  textContent: prompt,
                  createdAt: new Date().toISOString(),
                },
              ]
            : oldData
      );
    },
    onSettled: () => {
      setIsStreaming(false);
      setHasStreamed(true);
      queryClient
        .refetchQueries({
          queryKey: threadMessagesKeys.one({ companyId, threadId }),
        })
        .then(() => {
          setDisplayedText("");
        });
    },
  });

  const stopStreaming = () => {
    reset();
    setIsStreaming(false);
  };

  return {
    displayedText,
    startStreaming,
    stopStreaming,
    isStreaming,
    hasStreamed,
  };
};
